import React, { useEffect, useRef, useState, useMemo } from "react";
import { Filter, Trophy, UserPlus } from "lucide-react";
import "./CompetitionLeaderboard.css";
import UserDetailPopup from "./UserDetailPopup";
import FollowPopup from "./FollowPopup";

function CompetitionLeaderboard({
  entries: entriesProp = [],
  oppositeMonthTop3: oppositeMonthTop3Prop = [],
  loading = false,
  error = "",
  monthLabel = "",
  texts = {},
  tableLabels = {},
  onRetry,
  profileDefault,
  variant = "modal",
  active = true,
  disabled = false,
  overlayMessage = "",
  onClick,
  lang = "en",
  onFollow,
  currentUser,
  refreshLeaderboard,
}) {
  // Ensure entries is always an array
  const entries = Array.isArray(entriesProp) ? entriesProp : [];
  const oppositeMonthTop3 = Array.isArray(oppositeMonthTop3Prop) ? oppositeMonthTop3Prop : [];
  const hasEntries = Array.isArray(entries) && entries.length > 0;
  const hasOppositeTop3 = oppositeMonthTop3.length > 0;
  
  const tableWrapperRef = useRef(null);
  const currentUserRowRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showFollowPopup, setShowFollowPopup] = useState(false);
  
  // Load filter state from localStorage on mount
  const [showFriendsOnly, setShowFriendsOnly] = useState(() => {
    if (typeof window !== "undefined") {
      try {
      const saved = localStorage.getItem("competitionLeaderboardFilterState");
      return saved === "true";
      } catch (e) {
        console.warn("Failed to read filter state from localStorage:", e);
        return false;
      }
    }
    return false;
  });
  
  // Reload filter state from localStorage once entries are available (in case localStorage wasn't ready on mount)
  // Only run once when entries become available, not when showFriendsOnly changes
  const hasReloadedFilter = useRef(false);
  useEffect(() => {
    if (!loading && hasEntries && Array.isArray(entries) && typeof window !== "undefined" && !hasReloadedFilter.current) {
      try {
        const saved = localStorage.getItem("competitionLeaderboardFilterState");
        if (saved === "true") {
          // Only update if we have friends, otherwise keep it false
          const hasAnyFriends = entries.some((entry) => Boolean(entry?.isFriend));
          if (hasAnyFriends) {
            setShowFriendsOnly(true);
          }
        }
        hasReloadedFilter.current = true;
      } catch (e) {
        console.warn("Failed to reload filter state from localStorage:", e);
        hasReloadedFilter.current = true;
      }
    }
  }, [loading, hasEntries, entries]);
  
  // Save filter state to localStorage whenever it changes (but not on initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (typeof window !== "undefined") {
      try {
      localStorage.setItem("competitionLeaderboardFilterState", String(showFriendsOnly));
      } catch (e) {
        console.warn("Failed to save filter state to localStorage:", e);
      }
    }
  }, [showFriendsOnly]);

  const {
    emptyLabel = "No scores yet.",
    retryLabel = "Retry",
  } = texts || {};

  const {
    rank: rankLabel = "#",
    player: playerLabel = "Player",
    score: scoreLabel = "Score",
  } = tableLabels || {};

  // Check if currentUser has any friends
  const hasFriends = useMemo(() => {
    if (!hasEntries || !currentUser) return false;
    return entries.some((entry) => Boolean(entry?.isFriend));
  }, [entries, hasEntries, currentUser]);

  // If no friends, always show unfiltered state (only after entries are loaded)
  useEffect(() => {
    // Only reset if entries have been loaded (not during initial mount when entries might be empty)
    if (!loading && hasEntries && !hasFriends && showFriendsOnly) {
      setShowFriendsOnly(false);
      if (typeof window !== "undefined") {
        try {
        localStorage.setItem("competitionLeaderboardFilterState", "false");
        } catch (e) {
          console.warn("Failed to save filter state to localStorage:", e);
        }
      }
    }
  }, [hasFriends, showFriendsOnly, loading, hasEntries]);

  // Filter entries to show only top 50 + current user + all friends with proper ranking
  // If showFriendsOnly is true, only show friends and currentUser
  const displayedEntries = useMemo(() => {
    if (!hasEntries) {
      return [];
    }

    // Create entries with their original index (for ranking)
    const entriesWithIndex = entries.map((entry, index) => ({
      ...entry,
      _originalIndex: index,
    }));

    // If filter is active, only show friends and currentUser
    if (showFriendsOnly) {
      return entriesWithIndex
        .filter((entry) => Boolean(entry?.isFriend) || Boolean(entry?.isCurrentUser))
        .sort((a, b) => {
          const indexA = a._originalIndex ?? 0;
          const indexB = b._originalIndex ?? 0;
          return indexA - indexB;
        });
    }

    const top50 = entriesWithIndex.slice(0, 50);
    const currentUserEntry = entriesWithIndex.find((entry) => Boolean(entry?.isCurrentUser));
    const currentUserIndex = currentUserEntry ? currentUserEntry._originalIndex : -1;

    // Get all friends (excluding current user and those already in top 50)
    const friendEntries = entriesWithIndex.filter((entry) => {
      const isFriend = Boolean(entry?.isFriend);
      const isCurrent = Boolean(entry?.isCurrentUser);
      const index = entry._originalIndex;
      
      // Include friends that are not current user and not already in top 50
      return isFriend && !isCurrent && index >= 50;
    });

    // Build the result: top 50 + current user (if not in top 50) + all friends
    let result = [...top50];

    // Collect all entries that should be shown below top 50 (currentUser + friends)
    const belowTop50 = [];
    
    // Add current user if not in top 50
    if (currentUserEntry && currentUserIndex >= 50) {
      belowTop50.push(currentUserEntry);
    }

    // Add all friends (they're already filtered to exclude top 50 and current user)
    belowTop50.push(...friendEntries);

    // Sort belowTop50 by their original index (which represents their ranking/score order)
    belowTop50.sort((a, b) => {
      const indexA = a._originalIndex ?? 0;
      const indexB = b._originalIndex ?? 0;
      return indexA - indexB;
    });

    // Add sorted entries below top 50
    result.push(...belowTop50);

    return result;
  }, [entries, hasEntries, showFriendsOnly]);

  useEffect(() => {
    if (!active || loading) {
      return;
    }
    const container = tableWrapperRef.current;
    const currentRow = currentUserRowRef.current;
    if (!container) {
      return;
    }
    if (currentRow) {
      const offset =
        currentRow.offsetTop -
        container.clientHeight / 2 +
        currentRow.clientHeight / 2;
      container.scrollTop = Math.max(offset, 0);
    } else {
      container.scrollTop = 0;
    }
  }, [active, loading, entries]);

  const wrapperClassName = [
    "competition-leaderboard__table-wrapper",
    disabled ? "competition-leaderboard__table-wrapper--disabled" : "",
    disabled && onClick ? "competition-leaderboard__table-wrapper--clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const innerClassName = [
    "competition-leaderboard__inner",
    disabled ? "competition-leaderboard__inner--blurred" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Map: userId -> { rank: 1|2|3, score: number }
  // NOTE: If opposite-month score is 0 (or missing), we do NOT show a trophy.
  // Rank is now based on score (handles ties: users with same score get same rank)
  const oppositeMonthTop3Map = useMemo(() => {
    const map = new Map();
    oppositeMonthTop3.forEach((entry) => {
      const userId = entry?.id || entry?.objectId;
      if (!userId) return;
      const score = Number(entry?.monthlyScore) || 0;
      if (score <= 0) return;
      // Use rank from entry (assigned in cloud function based on score, not index)
      const rank = entry?.rank || null;
      if (rank && rank >= 1 && rank <= 3) {
        map.set(userId, { rank, score });
      }
    });
    return map;
  }, [oppositeMonthTop3]);

  return (
    <div className={`competition-leaderboard competition-leaderboard--${variant}`}>
      {monthLabel && (
        <div className="competition-leaderboard__title-wrapper">
        <div className="competition-leaderboard__title">{monthLabel}</div>
          <div className="competition-leaderboard__title-buttons">
            {hasFriends && (
              <button
                type="button"
                className={`competition-leaderboard__filter-button${showFriendsOnly ? " competition-leaderboard__filter-button--active" : ""}`}
                onClick={() => setShowFriendsOnly(!showFriendsOnly)}
                aria-label={showFriendsOnly ? (lang === "nl" ? "Toon alle spelers" : "Show all players") : (lang === "nl" ? "Toon alleen vrienden" : "Show friends only")}
              >
                <Filter size={16} strokeWidth={2.5} />
              </button>
            )}
            {onFollow && currentUser && variant === "modal" && (
              <button
                type="button"
                className="competition-leaderboard__follow-button"
                onClick={() => setShowFollowPopup(true)}
                aria-label={lang === "nl" ? "Volg gebruiker" : "Follow user"}
              >
                <UserPlus size={16} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      )}

      <div 
        className={wrapperClassName} 
        ref={tableWrapperRef}
        onClick={disabled && onClick ? (event) => {
          event.stopPropagation();
          onClick(event);
        } : undefined}
        style={disabled && onClick ? { cursor: "pointer" } : undefined}
      >
        <div className={innerClassName}>
          {loading ? (
            <div className="competition-leaderboard__loading-container">
              <div className="competition-leaderboard__status">
                <div className="competition-leaderboard__spinner"></div>
              </div>
              {/* Skeleton table to reserve space and prevent modal from growing */}
              <table className="competition-leaderboard__table competition-leaderboard__table--skeleton">
                <thead>
                  <tr>
                    <th className="competition-leaderboard__header competition-leaderboard__header--rank">
                      {rankLabel}
                    </th>
                    <th className="competition-leaderboard__header competition-leaderboard__header--player">
                      {playerLabel}
                    </th>
                    <th className="competition-leaderboard__header competition-leaderboard__header--score">
                      {scoreLabel}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(5)].map((_, index) => (
                    <tr key={index} className="competition-leaderboard__row competition-leaderboard__row--skeleton">
                      <td className="competition-leaderboard__cell competition-leaderboard__cell--rank">
                        <span className="competition-leaderboard__skeleton-text"></span>
                      </td>
                      <td className="competition-leaderboard__cell competition-leaderboard__cell--player">
                        <div className="competition-leaderboard__skeleton-avatar"></div>
                        <span className="competition-leaderboard__skeleton-text"></span>
                      </td>
                      <td className="competition-leaderboard__cell competition-leaderboard__cell--score">
                        <span className="competition-leaderboard__skeleton-text"></span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            <div className="competition-leaderboard__status">
              <span>{error}</span>
              {onRetry && (
                <button
                  type="button"
                  className="competition-leaderboard__retry"
                  onClick={onRetry}
                >
                  {retryLabel}
                </button>
              )}
            </div>
          ) : hasEntries ? (
            <table className="competition-leaderboard__table">
              <thead>
                <tr>
                  <th className="competition-leaderboard__header competition-leaderboard__header--rank">
                    {rankLabel}
                  </th>
                  <th className="competition-leaderboard__header competition-leaderboard__header--player">
                    {playerLabel}
                  </th>
                  <th className="competition-leaderboard__header competition-leaderboard__header--score">
                    {scoreLabel}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedEntries.map((entry, displayIndex) => {
                  const avatarSrc = entry?.avatarUrl || profileDefault || "";
                  const isCurrent = Boolean(entry?.isCurrentUser);
                  const isFriend = Boolean(entry?.isFriend);
                  
                  // Use the original index + 1 for the actual rank (as if the whole table exists)
                  const actualRank = (entry?._originalIndex ?? displayIndex) + 1;
                  
                  // Check if this entry is in the opposite month top 3
                  const entryId = entry?.id || entry?.objectId;
                  const oppositeMonthMeta = entryId ? oppositeMonthTop3Map.get(entryId) : null;
                  const oppositeMonthRank = oppositeMonthMeta?.rank ?? null;
                  const oppositeMonthScore = oppositeMonthMeta?.score ?? 0;
                  const showOppositeMonthTrophy = Boolean(oppositeMonthRank) && oppositeMonthScore > 0;
                  
                  // Apply current user styling to both current user and friends
                  const shouldHighlight = isCurrent || isFriend;

                  // Build className
                  let rowClassName = `competition-leaderboard__row`;
                  if (shouldHighlight) {
                    rowClassName += " competition-leaderboard__row--current";
                  }
                  if ((variant === "result" || variant === "modal") && !disabled) {
                    rowClassName += " competition-leaderboard__row--clickable";
                  }

                  return (
                    <tr
                      key={entry?.id || `${entry?.name || "player"}-${displayIndex}`}
                      className={rowClassName}
                      ref={isCurrent ? currentUserRowRef : undefined}
                      onClick={(variant === "result" || variant === "modal") && !disabled ? (event) => {
                        event.stopPropagation();
                        // Add opposite month rank to entry if available
                        const entryWithOppositeRank = {
                          ...entry,
                          oppositeMonthRank: showOppositeMonthTrophy ? oppositeMonthRank : null,
                          oppositeMonthScore: showOppositeMonthTrophy ? oppositeMonthScore : 0,
                        };
                        setSelectedUser(entryWithOppositeRank);
                      } : undefined}
                      style={(variant === "result" || variant === "modal") && !disabled ? { cursor: "pointer" } : undefined}
                    >
                      <td className={`competition-leaderboard__cell competition-leaderboard__cell--rank${
                        entry?.playedToday 
                          ? " competition-leaderboard__cell--rank--played-today" 
                          : entry?.playedFuture 
                            ? " competition-leaderboard__cell--rank--played-future" 
                            : ""
                      }`}>
                        {actualRank}
                      </td>
                      <td className="competition-leaderboard__cell competition-leaderboard__cell--player">
                        <img
                          src={avatarSrc}
                          alt=""
                          className="competition-leaderboard__avatar"
                          aria-hidden="true"
                        />
                        <span
                          className={
                            shouldHighlight
                              ? "competition-leaderboard__player competition-leaderboard__player--current"
                              : "competition-leaderboard__player"
                          }
                        >
                          {entry?.name || "Player"}
                          {showOppositeMonthTrophy && (
                            <span className="competition-leaderboard__trophy" title={lang === "nl" ? `Top ${oppositeMonthRank} vorige maand` : `Top ${oppositeMonthRank} last month`}>
                              <Trophy size={20} strokeWidth={2} />
                              <span className="competition-leaderboard__trophy-rank">{oppositeMonthRank}</span>
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="competition-leaderboard__cell competition-leaderboard__cell--score">
                        <span
                          className={
                            shouldHighlight
                              ? "competition-leaderboard__score competition-leaderboard__score--current"
                              : "competition-leaderboard__score"
                          }
                        >
                          {entry?.monthlyScore ?? 0}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="competition-leaderboard__status">{emptyLabel}</div>
          )}
        </div>
        {disabled && overlayMessage && (
          <div className="competition-leaderboard__overlay">
            <span>{overlayMessage}</span>
          </div>
        )}
      </div>
      
      {selectedUser && (
        <UserDetailPopup
          user={selectedUser}
          monthLabel={monthLabel}
          profileDefault={profileDefault}
          onClose={() => setSelectedUser(null)}
          lang={lang}
          onFollow={onFollow}
          currentUser={currentUser}
          refreshLeaderboard={refreshLeaderboard}
        />
      )}
      
      {onFollow && (
        <FollowPopup
          visible={showFollowPopup}
          onClose={() => setShowFollowPopup(false)}
          onFollow={async (usernameOrEmail) => {
            await onFollow(usernameOrEmail);
            setShowFollowPopup(false);
            if (refreshLeaderboard) {
              refreshLeaderboard();
            }
          }}
          lang={lang}
        />
      )}
    </div>
  );
}

export default CompetitionLeaderboard;

